import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import BookingDetailsDrawer from './BookingDetailsDrawer';

const LANE_ROW_MIN_HEIGHT = 60;

function bookingDisplayRange(booking) {
  const start = moment(booking.check_in_date || booking.start || booking.start_date).startOf('day');
  const end = moment(booking.check_out_date || booking.end || booking.end_date).startOf('day');
  return { start, end };
}

function dateInBookingDisplayRange(dateKey, booking) {
  const d = moment(dateKey).startOf('day');
  const { start, end } = bookingDisplayRange(booking);
  if (!start.isValid() || !end.isValid()) return false;
  return d.isSameOrAfter(start, 'day') && d.isSameOrBefore(end, 'day');
}

function intervalsOverlapInclusive(aStart, aEnd, bStart, bEnd) {
  return aStart.isSameOrBefore(bEnd, 'day') && bStart.isSameOrBefore(aEnd, 'day');
}

/** Greedy lane packing: overlapping intervals use different rows; disjoint intervals may share a row. */
function assignBookingLanes(bookings) {
  if (!bookings.length) return [[]];

  const normalized = bookings
    .map((booking) => {
      const { start, end } = bookingDisplayRange(booking);
      return { booking, start, end };
    })
    .filter((nb) => nb.start.isValid() && nb.end.isValid());

  normalized.sort((a, b) => {
    const cmp = a.start.valueOf() - b.start.valueOf();
    if (cmp !== 0) return cmp;
    return b.end.diff(a.end);
  });

  const lanes = [];
  for (const nb of normalized) {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      const lane = lanes[i];
      const conflict = lane.some((other) =>
        intervalsOverlapInclusive(nb.start, nb.end, other.start, other.end),
      );
      if (!conflict) {
        lane.push(nb);
        placed = true;
        break;
      }
    }
    if (!placed) lanes.push([nb]);
  }

  return lanes.map((lane) => lane.map((nb) => nb.booking));
}

function getBookingBarPalette(booking) {
  const s = String(booking.status || '').toLowerCase();
  if (s === 'cancelled' || s === 'canceled' || s === 'no_show') {
    return { backgroundColor: '#e53935', muted: true };
  }
  if (s === 'checked_out') {
    return { backgroundColor: '#6c757d', muted: true };
  }
  if (s === 'partial_checkout') {
    return { backgroundColor: '#43a047', muted: false };
  }
  if (s === 'checked_in') {
    return { backgroundColor: '#4caf50', muted: false };
  }
  if (s === 'partial_checkin') {
    return { backgroundColor: '#81c784', muted: false };
  }
  if (s === 'confirmed') {
    return { backgroundColor: '#2196f3', muted: false };
  }
  if (s === 'reserved') {
    return { backgroundColor: '#5c6bc0', muted: false };
  }
  if (s === 'pending') {
    return { backgroundColor: '#ff9800', muted: false };
  }
  if (s === 'blocked' || s === 'block') {
    return { backgroundColor: '#c62828', muted: true };
  }
  return { backgroundColor: '#90a4ae', muted: false };
}

/**
 * Improved Booking Calendar Full View Component
 * Uses Material-UI components with responsive design
 * Similar to MUI Data Grid time-off calendar demo
 */
const BookingCalendarFullViewImproved = ({ data, bookingType = 'nightly', theme = 'light' }) => {
  const { rooms = [], bookings = [], start_date, end_date } = data || {};

  const isDark = theme === 'dark';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const dates = useMemo(() => {
    if (!start_date || !end_date) return [];
    const datesArray = [];
    const start = moment(start_date);
    const end = moment(end_date);
    const current = moment(start);

    while (current.isSameOrBefore(end, 'day')) {
      datesArray.push({
        key: current.format('YYYY-MM-DD'),
        date: current.clone(),
        display: current.format('ddd'),
        day: current.format('D'),
        month: current.format('MMM'),
      });
      current.add(1, 'day');
    }
    return datesArray;
  }, [start_date, end_date]);

  const bookingsByRoomAndDate = useMemo(() => {
    const result = {};
    const assignedBookings = new Set();

    if (!bookings || !dates.length) return result;

    bookings.forEach((booking) => {
      const { start: bookingStart, end: bookingEnd } = bookingDisplayRange(booking);
      if (!bookingStart.isValid() || !bookingEnd.isValid()) return;

      const paintDate = (roomId, dateCheck) => {
        if (!result[roomId]) result[roomId] = {};
        const dateKey = dateCheck.format('YYYY-MM-DD');
        if (!dates.find((d) => d.key === dateKey)) return;
        if (!result[roomId][dateKey]) result[roomId][dateKey] = [];
        const exists = result[roomId][dateKey].some((b) => b.id === booking.id);
        if (!exists) result[roomId][dateKey].push(booking);
      };

      if (booking.room_ids && booking.room_ids.length > 0) {
        booking.room_ids.forEach((roomId) => {
          let dateCheck = moment(bookingStart);
          while (dateCheck.isSameOrBefore(bookingEnd, 'day')) {
            paintDate(roomId, dateCheck);
            dateCheck.add(1, 'day');
          }
        });
        assignedBookings.add(booking.id);
      } else if (booking.room_type_ids && booking.room_type_ids.length > 0) {
        booking.room_type_ids.forEach((roomTypeId) => {
          const roomsOfType = rooms.filter((r) => r.room_type_id === roomTypeId);

          let assigned = false;
          for (const room of roomsOfType) {
            const bookingKey = `${booking.id}_${roomTypeId}`;
            if (assignedBookings.has(bookingKey)) {
              assigned = true;
              break;
            }

            let hasConflict = false;
            let dateCheck = moment(bookingStart);
            while (dateCheck.isSameOrBefore(bookingEnd, 'day')) {
              const dateKey = dateCheck.format('YYYY-MM-DD');
              if (dates.find((d) => d.key === dateKey)) {
                const existingBookings = result[room.id]?.[dateKey] || [];
                const conflict = existingBookings.some((existing) =>
                  dateInBookingDisplayRange(dateKey, existing),
                );
                if (conflict) {
                  hasConflict = true;
                  break;
                }
              }
              dateCheck.add(1, 'day');
            }

            if (!hasConflict) {
              dateCheck = moment(bookingStart);
              while (dateCheck.isSameOrBefore(bookingEnd, 'day')) {
                paintDate(room.id, dateCheck);
                dateCheck.add(1, 'day');
              }
              assignedBookings.add(bookingKey);
              assigned = true;
              break;
            }
          }
        });
      }
    });
    return result;
  }, [bookings, dates, rooms]);

  const uniqueBookingsPerRoom = useMemo(() => {
    const out = {};
    Object.entries(bookingsByRoomAndDate).forEach(([roomId, byDate]) => {
      const m = new Map();
      Object.values(byDate).forEach((arr) => {
        arr.forEach((b) => m.set(b.id, b));
      });
      out[roomId] = Array.from(m.values());
    });
    return out;
  }, [bookingsByRoomAndDate]);

  const lanesByRoom = useMemo(() => {
    const out = {};
    rooms.forEach((room) => {
      const list = uniqueBookingsPerRoom[room.id] || [];
      out[room.id] = assignBookingLanes(list);
    });
    return out;
  }, [rooms, uniqueBookingsPerRoom]);

  const gridRows = useMemo(() => {
    const rows = [];
    rooms.forEach((room) => {
      const lanes = lanesByRoom[room.id] || [[]];
      const laneCount = Math.max(1, lanes.length);
      for (let i = 0; i < laneCount; i++) {
        rows.push({ room, laneIndex: i, laneBookings: lanes[i] || [] });
      }
    });
    return rows;
  }, [rooms, lanesByRoom]);

  if (!data || !rooms || rooms.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          color: isDark ? '#999' : '#757575',
        }}
      >
        <Typography variant="body1">No rooms available</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 300px)',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `150px repeat(${dates.length}, minmax(100px, 1fr))`,
          minWidth: 'fit-content',
          backgroundColor: isDark ? '#2a2a31' : '#ffffff',
          '@media (min-width: 600px)': {
            gridTemplateColumns: `180px repeat(${dates.length}, minmax(120px, 1fr))`,
          },
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 10,
            backgroundColor: isDark ? '#333246' : '#f5f5f5',
            borderRight: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
            borderBottom: `2px solid ${isDark ? '#444' : '#ddd'}`,
            p: 1.5,
            fontWeight: 600,
            fontSize: '0.875rem',
            color: isDark ? '#ddd' : '#646777',
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          Rooms ({rooms.length})
        </Box>
        {dates.map(({ key, display, day, month }) => (
          <Box
            key={key}
            sx={{
              borderRight: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
              borderBottom: `2px solid ${isDark ? '#444' : '#ddd'}`,
              p: 1.5,
              backgroundColor: isDark ? '#333246' : '#f5f5f5',
              textAlign: 'center',
              fontWeight: 600,
              fontSize: '0.75rem',
              color: isDark ? '#ddd' : '#646777',
              textTransform: 'uppercase',
            }}
          >
            <Box sx={{ fontSize: '0.875rem', mb: 0.5 }}>{display}</Box>
            <Box sx={{ fontSize: '0.75rem', opacity: 0.8 }}>
              {month} {day}
            </Box>
          </Box>
        ))}

        {gridRows.map(({ room, laneIndex, laneBookings }) => (
          <React.Fragment key={`${room.id}_lane_${laneIndex}`}>
            <Box
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 5,
                backgroundColor: isDark ? '#2a2a31' : '#fafafa',
                borderRight: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                p: 1.5,
                fontWeight: laneIndex === 0 ? 600 : 400,
                fontSize: '0.875rem',
                color: isDark ? '#ddd' : '#212121',
                display: 'flex',
                alignItems: 'center',
                minHeight: LANE_ROW_MIN_HEIGHT,
              }}
            >
              {laneIndex === 0 ? (
                <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                  {room.room_number || `Room ${room.id}`}
                </Typography>
              ) : (
                <Typography component="span" variant="body2" sx={{ pl: 2, opacity: 0.75 }}>
                  ↳
                </Typography>
              )}
            </Box>
            {dates.map(({ key }, dateIndex) => {
              const here = laneBookings.filter((b) => dateInBookingDisplayRange(key, b));
              const booking = here[0];

              return (
                <Box
                  key={key}
                  sx={{
                    borderRight: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                    borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                    p: 0.5,
                    minHeight: LANE_ROW_MIN_HEIGHT,
                    height: LANE_ROW_MIN_HEIGHT,
                    backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                    position: 'relative',
                    overflow: 'visible',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      borderRight: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                      borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                      pointerEvents: 'none',
                      zIndex: 0,
                    },
                  }}
                >
                  {booking &&
                    (() => {
                      const bookingStart = bookingDisplayRange(booking).start;
                      const bookingEnd = bookingDisplayRange(booking).end;
                      const currentDate = moment(key);

                      const isStart = currentDate.isSame(bookingStart, 'day');
                      const isEnd = currentDate.isSame(bookingEnd, 'day');
                      const isMiddle =
                        currentDate.isAfter(bookingStart, 'day') &&
                        currentDate.isBefore(bookingEnd, 'day');
                      const isSingleDay = isStart && isEnd;

                      const showBookingInfo = isStart || isSingleDay;

                      let left = '0%';
                      let width = '100%';
                      let borderRadius = '38px';

                      if (isSingleDay) {
                        left = '25%';
                        width = '50%';
                        borderRadius = '38px';
                      } else if (isStart && !isEnd) {
                        left = '50%';
                        width = '50%';
                        borderRadius = '38px 0 0 38px';
                      } else if (isEnd && !isStart) {
                        left = '0%';
                        width = '50%';
                        borderRadius = '0 38px 38px 0';
                      } else if (isMiddle) {
                        left = '0%';
                        width = '100%';
                        borderRadius = '0';
                      }

                      const continuesNext =
                        dateIndex < dates.length - 1 && currentDate.isBefore(bookingEnd, 'day');
                      const continuesPrev = dateIndex > 0 && currentDate.isAfter(bookingStart, 'day');

                      const checkoutDate =
                        booking.check_out_date || booking.end_date || booking.end;
                      const isPastCheckout =
                        checkoutDate && moment(checkoutDate).isBefore(moment(), 'day');

                      const { backgroundColor, muted } = getBookingBarPalette(booking);
                      const isDisabled = muted || isPastCheckout;

                      return (
                        <Box
                          key={`${booking.id}_${key}_lane_${laneIndex}`}
                          sx={{
                            position: 'absolute',
                            top: 2,
                            left,
                            width,
                            height: `${LANE_ROW_MIN_HEIGHT - 4}px`,
                            zIndex: 10,
                            pointerEvents: 'auto',
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor,
                              color: '#ffffff',
                              borderRadius,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              textAlign: 'center',
                              padding: '4px 8px',
                              zIndex: 10,
                              opacity: isDisabled ? 0.55 : 1,
                              filter: isDisabled ? 'grayscale(0.35)' : 'none',
                              ...(continuesNext && {
                                borderRight: 'none',
                                marginRight: '-1px',
                              }),
                              ...(continuesPrev && {
                                marginLeft: '-1px',
                              }),
                            }}
                            onClick={() => {
                              setSelectedBookingId(booking.id);
                              setDrawerOpen(true);
                            }}
                          >
                            {showBookingInfo && (
                              <Box
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  width: '100%',
                                  lineHeight: 1.2,
                                }}
                              >
                                {booking.guest_name || 'Guest'}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })()}
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>

      <BookingDetailsDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedBookingId(null);
        }}
        bookingId={selectedBookingId}
        theme={theme}
      />
    </Box>
  );
};

export default BookingCalendarFullViewImproved;
